/**
 * PROJECT.ACTIONS
 */

import {
  Project,
  ProjectAxisRule,
  ProjectDiffusionMode,
  ProjectMessageOptions,
  ProjectQrcodeOptions,
  ProjectSmsOptions,
  ProjectStep,
  PROJECT_ACTIVATE,
  PROJECT_EDIT,
  PROJECT_EDIT_ALERTS,
  PROJECT_EDIT_AXIS_RULE,
  PROJECT_EDIT_MESSAGE_OPTIONS,
  PROJECT_EDIT_QRCODE_OPTIONS,
  PROJECT_GET,
  PROJECT_INIT,
  PROJECT_REMOVE,
  PROJECT_STATUS,
  StepAlert,
  PROJECT_EDIT_SMS_OPTIONS
} from "./project.types"
import { Template } from "./template.types"
import { StatusType } from "./_status.types"
import { Population } from "./population.types"
import { Topic } from "./topic.types"
import { v4 as uuid } from "uuid"
import { Axis } from "./axis.types"
import { flatten, sortBy, uniqBy } from "lodash"
import { Email } from "./email.types"
import { Sending } from "./sending.types"
import { User } from "./user.types"
import { Filter } from "./filter.types"
import { t } from "@/translate/t"
import { getTemplateIssues, TemplateIssue } from "@/redux/template-issues"
import { isReviewAiEnabled } from "@/utils/review-ai.utils"
import { store } from ".."
import { DisplayIssue } from "@/utils/issues-display.utils"
import i18n from "@/translate/i18n"
import { emailUpdate, emailUpdate_AsSupervisor } from "./email.actions"
import { formatUserExamples, getReachContacts } from "@/utils/recipient-reach.utils"
import { getFirstMissingLanguage, getMessageWarnings, isSmsSenderValid } from "@/utils/project-step-checks.utils"

//REDUX

export const projectActivate = (project: Project) => ({
  type: PROJECT_ACTIVATE,
  payload: {
    project
  }
})

export const projectEdit = (key: string, value: any) => ({
  type: PROJECT_EDIT,
  payload: {
    key,
    value
  }
})

export const projectEditAlerts = (key: ProjectStep, value: StepAlert) => ({
  type: PROJECT_EDIT_ALERTS,
  payload: {
    key,
    value
  }
})

export const projectEditAxisRule = (axisId: string, populations: Population[]) => ({
  type: PROJECT_EDIT_AXIS_RULE,
  payload: {
    axisId,
    populations
  }
})

export const projectEditMessageOptions = (key: string, value: any) => ({
  type: PROJECT_EDIT_MESSAGE_OPTIONS,
  payload: {
    key,
    value
  }
})

export const projectEditQrcodeOptions = (key: string, value: any) => ({
  type: PROJECT_EDIT_QRCODE_OPTIONS,
  payload: {
    key,
    value
  }
})

export const projectEditSmsOptions = (key: string, value: any) => ({
  type: PROJECT_EDIT_SMS_OPTIONS,
  payload: {
    key,
    value
  }
})

export const projectGet = (projects: Project[]) => ({
  type: PROJECT_GET,
  payload: {
    projects
  }
})

export const projectInit = () => ({
  type: PROJECT_INIT
})

export const projectRemove = (id: string) => ({
  type: PROJECT_REMOVE,
  payload: {
    id
  }
})

export const projectStatus = (status: StatusType) => ({
  type: PROJECT_STATUS,
  payload: {
    status
  }
})

//API

export const projectDestroy = (projectId: string) => ({
  type: "API",
  payload: {
    method: "DELETE",
    url: "/project/" + projectId
  }
})

export const projectFetchAll = () => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/projects"
  }
})

export const projectFetchOne = (projectId: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/public/project",
    data: {
      projectId
    }
  }
})

export const projectLaunch = (
  projectId: string,
  isTest: boolean,
  surveyName: string,
  language: string,
  emailId: string | null
) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/project/launch",
    data: {
      projectId,
      isTest,
      surveyName,
      language,
      emailId
    }
  }
})

export const projectUpdate = (project: Project) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/project",
    data: {
      project
    }
  }
})

export const projectUpdate_AsSupervisor = (project: Project, accountId: string) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/project",
    data: {
      project,
      accountId
    }
  }
})

//CHECK MODES
export const projectCheckModes: Function = (isDiffusionModeDefined: boolean) => async (dispatch) => {
  const errors: string[] = []

  if (!isDiffusionModeDefined) {
    errors.push("project_error_modes_no_mode")
  }

  dispatch(projectEditAlerts("modes", new StepAlert({ errors, warnings: [] })))
}

//Where to fix an issue of the questionnaire : the topic (and the language) in the editor,
//the parameters of the template for the labels of the scales, otherwise the editor
function getIssueTarget(issue: TemplateIssue): string {
  if (issue.topicId) return "topic:" + issue.topicId + (issue.language ? "|" + issue.language : "")
  return issue.kind === "scale-labels" || issue.kind === "scale-labels-missing" ? "params" : "editor"
}

//CHECK TEMPLATE
export const projectCheckTemplate: Function =
  (template: Template, topics: Topic[], axes: Axis[], languages: string[]) => async (dispatch, getState) => {
    const errors: string[] = []
    const warnings: string[] = []
    const ignored: string[] = []
    const targets: { [message: string]: string } = {}
    const issues: DisplayIssue[] = []
    const issueMessagesList: string[] = []
    const aiMessages: string[] = [] //Warnings that come from the AI (the assistant shows a small logo)
    let unnamedTopicsMessage = ""

    //Type topics et axes
    topics = topics.map((x) => new Topic(x))
    //Same order as the editor of the questionnaire (the number of an axis in a message is its place there)
    const axesOrder: string[] = template.axesOrder || []
    axes = sortBy(
      axes.map((x) => new Axis(x)),
      (axis) => axesOrder.indexOf(axis.id)
    )

    //indicators to evaluate
    const topicsCount: number = topics.length
    const axesCount: number = axes.length

    //check errors
    if (!template.id) {
      errors.push("project_error_template_no_template")
      targets[t("project_error_template_no_template")] = "focus:template-select" //the button to choose a questionnaire
    } else {
      //Topics without any name : red flag, blocking
      const unnamedTopicsCount = topics.filter((x) => x.isUnnamedCritical(languages)).length
      if (unnamedTopicsCount > 0) {
        const message = t("project_error_template_topic_unnamed" + (unnamedTopicsCount > 1 ? "_multiple" : ""), {
          count: unnamedTopicsCount
        })
        errors.push(message)
        unnamedTopicsMessage = message
        targets[message] = "topic:" + topics.find((x) => x.isUnnamedCritical(languages))?.id
      }

      //Problems of the questionnaire : errors block the launch, warnings are only displayed
      const session = getState()._session
      getTemplateIssues(topics, template.options, languages, {
        axes,
        rps: session.modules.survey.rps || session.interfaceType === "SUPERVISOR",
        ai: isReviewAiEnabled(session.modules.survey.projectAssistant)
      }).forEach((issue) => {
        if (issue.kind === "reverse-suspect") aiMessages.push(issue.message)
        //Same display as the editor : the issue with where to fix it
        issueMessagesList.push(issue.message)
        issues.push({
          id: issue.id,
          kind: issue.kind,
          severity: issue.severity,
          label: issue.label,
          description: issue.description,
          group: issue.group,
          intro: issue.intro,
          details: issue.details,
          target: getIssueTarget(issue),
          ignored: issue.severity === "warning" && (template.options.dismissedIssues || []).includes(issue.id)
        })
        if (issue.severity === "warning") {
          //A warning ignored in the questionnaire editor stays visible here, greyed
          if ((template.options.dismissedIssues || []).includes(issue.id)) {
            ignored.push(issue.message)
          } else {
            warnings.push(issue.message)
          }
          //Where to fix it : the topic (and the language) in the editor, or the editor itself
          targets[issue.message] = getIssueTarget(issue)
        } else if (issue.kind !== "unnamed-topic") {
          errors.push(issue.message)
          targets[issue.message] = getIssueTarget(issue)
        }
      })

      //Errors about the questionnaire
      if (axesCount < 1) {
        errors.push("project_error_template_no_axis")
        targets[t("project_error_template_no_axis")] = "editor" //the editor, to add the axes and the questions
      }
      if (topicsCount < 1) {
        errors.push("project_error_template_no_question")
        targets[t("project_error_template_no_question")] = "editor"
      }
    }

    //The other errors (no template, no axis, question without text, ...) : lines without detail, after the issues
    //(the message about the topics without name is replaced by one issue per topic)
    const issueMessages = [...issueMessagesList, ...(unnamedTopicsMessage ? [unnamedTopicsMessage] : [])]
    errors
      .filter((message) => !issueMessages.includes(message))
      .forEach((message, i) => {
        const text = t(message)
        issues.push({ id: "plain-" + i, severity: "error", label: text, description: "", target: targets[text] })
      })

    dispatch(projectEditAlerts("template", new StepAlert({ errors, warnings, ignored, targets, issues, ai: aiMessages })))
  }

//CHECK RECIPIENTS
export const projectCheckRecipients: Function =
  (
    usersWithoutEmail: User[],
    countWithoutEmail: number,
    usersWithoutAddress: User[],
    countWithoutAddress: number,
    usersCount: number,
    participationMin: number,
    usersUnreachable: User[] = [],
    countUnreachable: number = 0,
    diffusionMode: { email?: boolean; sms?: boolean; qrcode?: boolean } | null = null,
    qrcodeOptions: { hideAddress?: boolean } | null = null,
    countToFix: number = 0,
    countLanguageAbsent: number = 0,
    projectLanguages: string[] = []
  ) =>
  async (dispatch) => {
    const errors: string[] = []
    const warnings: string[] = []
    const targets: { [message: string]: string } = {} //The lines about users lead to the list of the recipients, where their file can be edited

    //If user count is null
    if (usersCount === 0) {
      errors.push("project_error_recipients_no_user")
      targets[t("project_error_recipients_no_user")] = "focus:recipients-mode" //the cards : invite everyone or a selection
    }

    //If there is not enough users
    if (usersCount < participationMin) {
      const tooFewMessage = t("project_error_recipients_too_few_users", {
        count: usersCount,
        min: participationMin
      })
      warnings.push(tooFewMessage)
      targets[tooFewMessage] = "focus:recipients-mode" //the cards : invite everyone or a selection
    }

    //Users that no chosen mode can reach : nothing would be sent to them. Nobody reachable : red, the other messages say the same thing
    const reachContacts = getReachContacts(diffusionMode, qrcodeOptions)
    //One contact : "no phone number" ; several : "neither e-mail address, nor phone number" (the words come with the translation)
    const contacts =
      reachContacts.length === 1
        ? t("project_recipients_none_" + reachContacts[0])
        : reachContacts.map((contact) => t("project_recipients_no_" + contact)).join(", ")
    const isNobodyReachable = reachContacts.length > 0 && usersCount > 0 && countUnreachable >= usersCount

    if (isNobodyReachable) {
      const nobodyMessage = t("project_error_recipients_all_unreachable", { contacts })
      errors.push(nobodyMessage)
      targets[nobodyMessage] = "filter:unreachable" //the pencil of each file is in the list of the step
    } else if (reachContacts.length > 0 && countUnreachable > 0) {
      //A single mode (e-mail alone, QR code alone) : the message of that mode below already says it
      const isSaidBelow = reachContacts.length === 1 && reachContacts[0] !== "phone"
      if (!isSaidBelow) {
        const unreachableMessage =
          t("project_error_recipients_unreachable_users" + (countUnreachable > 1 ? "_multiple" : ""), {
            count: countUnreachable,
            contacts,
            list: formatUserExamples(usersUnreachable, countUnreachable, (list) =>
              t("project_error_recipients_list_example", { list })
            )
          })
        warnings.push(unreachableMessage)
        targets[unreachableMessage] = "filter:unreachable"
      }
    }

    //If there is users without email that are referenced
    if (countWithoutEmail > 0 && !isNobodyReachable) {
      const emaillessMessage =
        t("project_error_recipients_emailless_users" + (countWithoutEmail > 1 ? "_multiple" : ""), {
          count: countWithoutEmail,
          list: formatUserExamples(usersWithoutEmail, countWithoutEmail, (list) =>
            t("project_error_recipients_list_example", { list })
          )
        })
      warnings.push(emaillessMessage)
      targets[emaillessMessage] = "filter:noEmail"
    }

    //If there is users without adress
    if (countWithoutAddress > 0 && !isNobodyReachable) {
      const noAddressMessage =
        t("project_error_recipients_no_address_users" + (countWithoutAddress > 1 ? "_multiple" : ""), {
          count: countWithoutAddress,
          list: formatUserExamples(usersWithoutAddress, countWithoutAddress, (list) =>
            t("project_error_recipients_list_example", { list })
          )
        })
      warnings.push(noAddressMessage)
      targets[noAddressMessage] = "filter:noAddress"
    }

    //Files with an e-mail or a phone number that is not valid, or that is on several users : the link shows only those files
    if (countToFix > 0) {
      const toFixMessage = t("project_warning_recipients_to_fix" + (countToFix > 1 ? "_multiple" : ""), { count: countToFix })
      warnings.push(toFixMessage)
      targets[toFixMessage] = "filter:toFix"
    }

    //Users whose language is not one of the languages of the project : they would see the survey in the default language
    if (countLanguageAbsent > 0 && projectLanguages.length > 0) {
      const languageMessage = t("project_warning_recipients_language" + (countLanguageAbsent > 1 ? "_multiple" : ""), {
        count: countLanguageAbsent,
        languages: projectLanguages.map((language) => language.toUpperCase()).join(", ")
      })
      warnings.push(languageMessage)
      targets[languageMessage] = "step:languages"
    }

    dispatch(projectEditAlerts("recipients", new StepAlert({ errors, warnings, targets })))
  }

//CHECK IMPORT
//Files imported without any name or contact : nothing can be sent to them (the link shows these files in the list of the recipients)
export const projectCheckImport: Function = (countEmpty: number, countAccount: number = 2) => async (dispatch) => {
  const errors: string[] = []
  const warnings: string[] = []
  const targets: { [message: string]: string } = {}

  //Alone in the account : nobody to invite (the administrator is the only collaborator)
  if (countAccount < 2) {
    errors.push("project_error_import_no_user")
    targets[t("project_error_import_no_user")] = "focus:import-users" //the list with the button to import
  }

  if (countEmpty > 0) {
    const message = t("project_warning_import_empty" + (countEmpty > 1 ? "_multiple" : ""), { count: countEmpty })
    warnings.push(message)
    targets[message] = "filter:empty"
  }

  dispatch(projectEditAlerts("import", new StepAlert({ errors, warnings, targets })))
}

//CHECK SEGMENT
export const projectCheckSegment: Function =
  (segmentationByAxesRules: ProjectAxisRule[], populations: Population[]) => async (dispatch) => {
    const errors: string[] = []
    const warnings: string[] = []

    //Never called before the project assistant : kept as a warning (not blocking) until its real impact is measured
    if (checkAbsentPopulations().length > 0) {
      warnings.push("project_error_segment_missing_population")
    }

    function checkAbsentPopulations(): Population[] {
      const uselessPopulations: Population[] = []
      const axisPopulations: Population[] = uniqBy(
        flatten(segmentationByAxesRules.map((x: ProjectAxisRule) => x.populations)),
        "id"
      )

      axisPopulations.forEach((axisPop: Population) => {
        const commonFilterPopulations: Population[] = populations.filter(
          (projectPop: Population) => projectPop.filterName === axisPop.filterName
        )
        if (
          commonFilterPopulations.length > 0 &&
          !commonFilterPopulations.find((projectPop: Population) => projectPop.name === axisPop.name)
        ) {
          uselessPopulations.push(axisPop)
        }
      })

      return uselessPopulations
    }

    dispatch(projectEditAlerts("segment", new StepAlert({ errors, warnings })))
  }

//CHECK LANGUAGE
export const projectCheckLanguages: Function = (languages: string[]) => async (dispatch) => {
  const errors: string[] = []

  //check errors
  const targets: { [message: string]: string } = {}
  if (languages.length < 1) {
    errors.push("project_error_languages_no_language")
    targets[t("project_error_languages_no_language")] = "focus:project-languages"
  }

  dispatch(projectEditAlerts("languages", new StepAlert({ errors, warnings: [], targets })))
}

//CHECK FILTERS
export const projectCheckFilters: Function = (filters: Filter[], participationMin: number) => async (dispatch) => {
  const warnings: string[] = []

  //Detect if there is a lot of populations with less than n participants (more than 25%)
  filters.forEach((filter) => {
    const populationWithoutResults = filter.populations.filter((x) => x.usersCount < participationMin)
    if (filter.populations.length) {
      if (populationWithoutResults.length / filter.populations.length > 0.25) {
        const populationSample = populationWithoutResults.slice(0, 3)
        warnings.push(
          t("project_warning_filters", {
            name: filter.name,
            min: participationMin,
            populations:
              populationSample.map((x) => x.name).join(", ") + (populationWithoutResults.length > 3 ? "..." : "")
          })
        )
      }
    }
  })

  const targets: { [message: string]: string } = {}

  if (filters.some((filter: Filter) => !filter.isChecked && !filter.default)) {
    warnings.push("project_warning_filters_unchecked")
    targets[t("project_warning_filters_unchecked")] = "focus:filters-check"
  }

  dispatch(projectEditAlerts("filters", new StepAlert({ errors: [], warnings, targets })))
}

//CHECK MESSAGE
export const projectCheckMessage: Function =
  (languages: string[], messageOptions: ProjectMessageOptions) => async (dispatch) => {
    const errors: string[] = []
    const warnings: string[] = getMessageWarnings(languages, messageOptions)

    //Each alert leads to the field to fill (the language shown in the field is the one being edited)
    const fields: { [key: string]: string } = {
      project_warning_message_missing_title: "message-title",
      project_warning_message_missing_title_language: "message-title",
      project_warning_message_missing_content: "message-content",
      project_warning_message_missing_content_language: "message-content",
      project_warning_message_missing_info: "message-info",
      project_warning_message_missing_info_language: "message-info",
      project_warning_message_missing_confirmation: "message-confirmation-title",
      project_warning_message_missing_summary: "message-summary"
    }
    //The language to open is the first one in which the text is missing ("focus:<field>|<language>")
    const missingLanguage: { [key: string]: string | undefined } = {
      "message-title": getFirstMissingLanguage(languages, messageOptions.title),
      "message-content": getFirstMissingLanguage(languages, messageOptions.content),
      "message-info": getFirstMissingLanguage(languages, messageOptions.infoContent),
      "message-confirmation-title": getFirstMissingLanguage(
        languages,
        messageOptions.confirmationTitle,
        messageOptions.confirmationContent,
        messageOptions.confirmationSender
      ),
      "message-summary": getFirstMissingLanguage(languages, messageOptions.summaryContent)
    }
    const missingLanguages: { [field: string]: string[] } = {
      "message-title": languages.filter((language) => getFirstMissingLanguage([language], messageOptions.title)),
      "message-content": languages.filter((language) => getFirstMissingLanguage([language], messageOptions.content)),
      "message-info": languages.filter((language) => getFirstMissingLanguage([language], messageOptions.infoContent)),
      "message-confirmation-title": languages.filter((language) =>
        getFirstMissingLanguage([language], messageOptions.confirmationTitle, messageOptions.confirmationContent, messageOptions.confirmationSender)
      ),
      "message-summary": languages.filter((language) => getFirstMissingLanguage([language], messageOptions.summaryContent))
    }
    const targets: { [message: string]: string } = {}
    const languagesOfAlert: { [message: string]: string[] } = {}
    warnings.forEach((warning) => {
      const field: string | undefined = fields[warning]
      if (!field) return
      targets[t(warning)] = "focus:" + field + (languages.length > 1 && missingLanguage[field] ? "|" + missingLanguage[field] : "")
      if (languages.length > 1) languagesOfAlert[t(warning)] = missingLanguages[field]
    })

    dispatch(projectEditAlerts("message", new StepAlert({ errors, warnings, targets, languages: languagesOfAlert })))
  }

//CHECK QRCODE
export const projectCheckQrcode: Function =
  (languages: string[], qrcodeOptions: ProjectQrcodeOptions) => async (dispatch) => {
    const errors: string[] = []
    const warnings: string[] = []

    //Each alert leads to the field to fill (the step is open when its alerts are displayed)
    const targets: { [message: string]: string } = {}
    const languagesOfAlert: { [message: string]: string[] } = {}

    if (qrcodeOptions.senderAddress.length < 1 && !qrcodeOptions.hideAddress) {
      warnings.push("project_warning_qrcode_missing_address")
      targets[t("project_warning_qrcode_missing_address")] = "focus:qrcode-sender-address"
    }

    if (qrcodeOptions.senderName.length < 1) {
      warnings.push("project_warning_qrcode_missing_name")
      targets[t("project_warning_qrcode_missing_name")] = "focus:qrcode-sender-name"
    }

    const missingLanguage: string | undefined = languages.find((lang: string) => !qrcodeOptions.content[lang] || qrcodeOptions.content[lang].length < 1)
    if (missingLanguage) {
      warnings.push("project_warning_qrcode_missing_content")
      //The text in the language where it is missing ("focus:<field>|<language>")
      targets[t("project_warning_qrcode_missing_content")] = "focus:qrcode-content" + (languages.length > 1 ? "|" + missingLanguage : "")
      if (languages.length > 1) languagesOfAlert[t("project_warning_qrcode_missing_content")] = languages.filter((lang: string) => !qrcodeOptions.content[lang] || qrcodeOptions.content[lang].length < 1)
    }

    dispatch(projectEditAlerts("qrcode", new StepAlert({ errors, warnings, targets, languages: languagesOfAlert })))
  }

//CHECK SMS
export const projectCheckSms: Function = (smsOptions: ProjectSmsOptions) => async (dispatch) => {
  const errors: string[] = []
  const warnings: string[] = []

  const targets: { [message: string]: string } = {}

  if (!smsOptions.sender || smsOptions.sender.length < 1) {
    warnings.push("project_warning_sms_missing_sender")
    targets[t("project_warning_sms_missing_sender")] = "focus:sms-sender"
  } else if (!isSmsSenderValid(smsOptions.sender)) {
    warnings.push("project_warning_sms_invalid_sender")
    targets[t("project_warning_sms_invalid_sender")] = "focus:sms-sender"
  }

  //Without a country code only the numbers written with the international prefix can receive the SMS
  if (!smsOptions.countryCode || smsOptions.countryCode.trim().length < 1) {
    warnings.push("project_warning_sms_missing_country_code")
    targets[t("project_warning_sms_missing_country_code")] = "focus:sms-country-code"
  }

  //The text is not prefilled and nothing can be sent without it : the only blocking point of the step (the sender and the country code stay warnings)
  if (!smsOptions.content || smsOptions.content.length < 1) {
    errors.push("project_error_sms_missing_content")
    targets[t("project_error_sms_missing_content")] = "focus:sms-content"
  }

  dispatch(projectEditAlerts("sms", new StepAlert({ errors, warnings, targets })))
}

//CHECK EMAIL
export const projectCheckEmails: Function = (languages: string[], emails: Email[]) => async (dispatch) => {
  const errors: string[] = []
  const warnings: string[] = []
  const targets: { [message: string]: string } = {}
  const languagesOfAlert: { [message: string]: string[] } = {}
  emails = emails.map((x) => new Email(x))

  if (emails.length === 0) {
    warnings.push("project_error_emails_no_email")
    targets[t("project_error_emails_no_email")] = "focus:emails-list" //the list with the link to add an e-mail
  }

  if (emails.some((email: Email) => !email.isChecked)) {
    warnings.push("project_warning_emails_not_checked")
    targets[t("project_warning_emails_not_checked")] = "focus:emails-check-all"
  }

  if (emails.some((email: Email) => checkFieldEmptiness(email))) {
    warnings.push("project_error_emails_empty_email")
    //The content of the e-mail, in the language to translate : the first e-mail with a missing text, in its first missing language
    const faulty: Email = emails.find((email: Email) => checkFieldEmptiness(email))!
    const language: string | undefined = languages.find((lang: string) => faulty.getEmptyFields(lang).length > 0)
    targets[t("project_error_emails_empty_email")] =
      "focus:emails-editor" + (languages.length > 1 && language ? "|" + language + "|" + faulty.id : "")

    if (languages.length > 1) languagesOfAlert[t("project_error_emails_empty_email")] = languages.filter((lang: string) => emails.some((email: Email) => email.getEmptyFields(lang).length > 0))
  }

  if (emails.some((email: Email) => email.name.length === 0 || email.sender.length === 0)) {
    errors.push("project_error_emails_empty_name")
  }

  function checkFieldEmptiness(email: Email): boolean {
    return languages.some((lang: string) => email.getEmptyFields(lang).length > 0)
  }

  dispatch(projectEditAlerts("emails", new StepAlert({ errors, warnings, targets, languages: languagesOfAlert })))
}

//CHECK CALENDAR
export const projectCheckCalendar: Function =
  (diffusionMode: ProjectDiffusionMode, sendings: Sending[]) => async (dispatch, getState) => {
    const errors: string[] = []
    const warnings: string[] = []

    //The alerts on the sendings lead to the pill to drag on the calendar
    const targets: { [message: string]: string } = {}
    const addSendingWarning = (message: string) => {
      warnings.push(message)
      targets[t(message)] = "focus:calendar-sending-chip"
    }

    if (diffusionMode.email) {
      if (sendings.length === 0) {
        addSendingWarning("project_error_calendar_no_sending")
      } else if (sendings.length === 1) {
        addSendingWarning("project_warning_calendar_no_sendings")
      } else if (sendings.length === 2) {
        addSendingWarning("project_warning_calendar_too_few_sendings_1")
      } else if (sendings.length < 5) {
        addSendingWarning(
          t("project_warning_calendar_too_few_sendings_2", {
            count: sendings.length - 1
          })
        )
      }
    }

    if (!getState().project.active.isLaunch) {
      if (
        sendings
          .map((sending: Sending) => sending.date)
          .sort((a: any, b: any) => {
            return a - b
          })[0] < new Date()
      ) {
        errors.push("project_error_calendar_sending_date_expired")
        targets[t("project_error_calendar_sending_date_expired")] = "focus:calendar-grid" //the calendar, to move the date
      }
    }

    dispatch(projectEditAlerts("calendar", new StepAlert({ errors, warnings, targets })))
  }

export async function projectCreateNew(
  accountName: string,
  projectName: string,
  template: Template | null,
  accountId: string | null
) {
  const project = new Project()
  project.id = uuid()
  project.languages = [i18n.language]
  project.name = projectName
  project.messageOptions = project.getInitialMessageOptions(accountName, projectName)
  project.smsOptions = {
    sender: t("project_invitation_sms_sender_default"),
    countryCode: "+33",
    content: t("project_invitation_sms_content_default")
  }

  if (template) {
    project.TemplateId = template.id
  }

  //Update project in the database
  if (accountId) {
    await store.dispatch(projectUpdate_AsSupervisor(project, accountId))
  } else {
    await store.dispatch(projectUpdate(project))
  }

  //Add emails from template
  const email = new Email()
  const emails: Email[] = [email.getFromMessage(t("email_template_0"), project.messageOptions, accountName)].concat(
    email
      .getList(
        accountName,
        project.name,
        project.Template.label /*.length > 0 ? project.Template.label : t("template_tag_qvt")*/
      )
      .slice(1)
  )

  //Update in database (emails)
  for (let i = 0; i < emails.length; i++) {
    if (accountId) {
      await store.dispatch(emailUpdate_AsSupervisor(project.id, emails[i], accountId))
    } else {
      await store.dispatch(emailUpdate(project.id, emails[i]))
    }
  }

  //Set order of the emails
  project.emailsOrder = emails.map((x) => x.id)

  //Activate project
  return project
}
